import { useEffect, useState } from "react";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { Button, Grid, Typography } from "@mui/material";
import type { PermissionState } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import { useChatTokenQuery } from "Api/Queries/Chat/useChatTokenQuery";
import { useChatPushDeviceToggle } from "Hooks/Chat/useChatPushDeviceToggle";
import { useStreamChatClient } from "Hooks/Chat/useStreamChatClient";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  getChatPushPermission,
  getChatPushPreference,
} from "Infrastructure/Capacitor/chatPushDevice";
import { Resources, useResource } from "Translations/Resources";
import { isAuthenticated } from "Utils/AuthUtils";
import styled from "styled-components";

const StyledGrid = styled(Grid)`
  color: ${(props) => props.theme.palette.text.secondary};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};

  &.MuiGrid-container {
    padding-left: 0;
    padding-right: 0;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  svg {
    height: 16px;
    width: 16px;
  }
`;

/**
 * "Chat notifications on this device" — the later opt-in for chat push on the native shells (the
 * first ask is the system prompt at the chat gate). Device-local state, so it reads as a property
 * of this phone, not the account. The row exists only to turn push ON: once the device is opted in
 * and the OS permission granted it disappears, and turning push off is left to the system settings.
 * A one-shot action that then goes away, so it is a button, not a switch that could never show "on".
 * Once the OS permission is denied only the system settings can undo it either, so the button is
 * disabled with a hint. Render only inside a native shell (`isNativePlatform()`).
 */
export const ChatPushDeviceSettingsRow = () => {
  const [permission, setPermission] = useState<PermissionState | null>(null);
  const [isEnabled, setIsEnabled] = useState(
    () => getChatPushPreference() === "on",
  );
  const { t } = useResource();
  const user = useLoggedInUser();
  const { data: chatToken } = useChatTokenQuery(
    isAuthenticated(user),
    user?.activeContextPublicID,
  );
  const { client } = useStreamChatClient(chatToken);
  const { toggle, isBusy } = useChatPushDeviceToggle(client);

  useEffect(() => {
    getChatPushPermission()
      .then(setPermission)
      .catch((error) =>
        Sentry.captureException(error, { tags: { feature: "chatPush" } }),
      );
  }, []);

  // Opted in and allowed: nothing left to do here. Also hidden while the permission is still being
  // read, so an opted-in device does not flash the row on every Settings visit.
  if (isEnabled && (permission === null || permission === "granted")) {
    return null;
  }

  const isDenied = permission === "denied";

  const handleTurnOn = async () => {
    const { permission, isEnabled } = await toggle(true);
    setPermission(permission);
    setIsEnabled(isEnabled);
  };

  return (
    <StyledGrid
      container
      display="flex"
      justifyContent="center"
      alignItems="center"
      py={4}
    >
      <Grid size={{ xs: 1 }}>
        <IconWrapper>
          <NotificationsNoneOutlinedIcon />
        </IconWrapper>
      </Grid>
      <Grid size={{ xs: 7 }}>
        <Typography fontSize={16}>
          {t(Resources.Chat.PushNotifications.DeviceToggleLabel)}
        </Typography>
        <Typography variant="body2">
          {isDenied
            ? t(Resources.Chat.PushNotifications.DeniedHint)
            : t(Resources.Chat.PushNotifications.DeviceToggleDescription)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 4 }} justifyContent="flex-end" display="flex">
        <Button
          size="small"
          variant="contained"
          loading={isBusy}
          disabled={isDenied || !client || permission === null}
          onClick={() => void handleTurnOn()}
        >
          {t(Resources.Common.TurnOn)}
        </Button>
      </Grid>
    </StyledGrid>
  );
};
